/**
 * Payment Schedule Calculator
 * Calculates installment payment schedules based on booking and departure dates
 */

/** Last installment must fall on or before (departure − N days). Slice Pay / standard installments. */
export const SLICE_PAY_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE = 14;

/** Defera Zero: matches subscription cancel_at and calculator. */
export const DEFERA_ZERO_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE = 21;

/** Defera Capital: all plans must finish at least this many days before departure (Stripe cancel_at aligns). */
export const DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE = 5;

/** Hold fee on (base + service fee), pre-seats. Slice Pay / standard installments. */
export const SLICE_PAY_HOLD_FEE_RATE = 0.15;

/**
 * Defera Capital hold fee on (base + service fee), pre-seats.
 * Matches Slice Pay's 15% going forward. Historically this was 0.12; bookings
 * created before the change have their own rate stored on
 * `flight_data.payment_schedule.hold_fee_rate` and are intentionally left as-is.
 */
export const DEFERA_CAPITAL_HOLD_FEE_RATE = 0.15;

/** Slice Pay / standard installments (plans require ≥22 days). */
export function getSlicePayDepositOptionsForLeadDays(daysUntilDeparture) {
  if (!Number.isFinite(daysUntilDeparture)) return [20, 30, 40, 50];
  if (daysUntilDeparture < 22) return [];
  if (daysUntilDeparture < 60) return [30, 40, 50];
  return [20, 30, 40, 50];
}

/**
 * Defera Capital only — tighter deposit tiers (plans require ≥22 days).
 * Effective 2026-06-16 for NEW bookings only — existing trips keep their locked
 * deposit % via `respectStoredDepositPercentage` and are not retroactively
 * re-snapped to a lower tier.
 *   120+   → 5 / 10 / 20 / 30 / 40 / 50
 *   90–119 → 5 / 10 / 20 / 30 / 40 / 50
 *   60–89  → 20 / 30 / 40 / 50
 *   30–59  → 40 / 50
 *   22–29  → 60 only (raised from 50, 2026-08-11 — close-in trips carry the
 *             most default risk, so the majority is collected up front)
 */
export function getDeferaCapitalDepositOptionsForLeadDays(daysUntilDeparture) {
  if (!Number.isFinite(daysUntilDeparture)) return [5, 10, 20, 30, 40, 50];
  if (daysUntilDeparture < 22) return [];
  if (daysUntilDeparture < 30) return [60];
  if (daysUntilDeparture < 60) return [40, 50];
  if (daysUntilDeparture < 90) return [20, 30, 40, 50];
  return [5, 10, 20, 30, 40, 50];
}

/**
 * Calculate payment schedule based on rules
 * @param {Object} params
 * @param {string} params.bookingDate - ISO date string (YYYY-MM-DD) or Date object
 * @param {string} params.departureDate - ISO date string (YYYY-MM-DD) or Date object
 * @param {number} params.totalWithFee - Total amount BEFORE the hold fee is added (base fare + service fee, plus any other pre-hold charges)
 * @param {number} params.depositPercentage - Selected deposit percentage (allowed tier for lead time: Slice Pay 20–50; Defera Capital 5–50 with 5%/10% only at 90+ day lead — see {@link getDeferaCapitalDepositOptionsForLeadDays})
 * @param {number} params.customDepositAmount - Optional custom deposit amount (overrides percentage calculation)
 * @param {number} params.firstPaymentDays - Optional. When 7 (new Defera Zero), first payment is 7 days out for both weekly and bi-weekly. Otherwise legacy: weekly=8, bi-weekly=14.
 * @param {number} params.daysBeforeDepartureForLastPayment - Optional. Days before departure for last allowed payment. Default 14 (Slice Pay). Use 21 for Defera Zero; use {@link DEFERA_CAPITAL_LAST_PAYMENT_DAYS_BEFORE_DEPARTURE} for Defera Capital.
 * @param {number} [params.holdFeeRate] - Fraction of pre-hold subtotal (e.g. {@link SLICE_PAY_HOLD_FEE_RATE} or {@link DEFERA_CAPITAL_HOLD_FEE_RATE}). Default: Slice rate.
 * @param {boolean} [params.deferaCapitalDepositTiers] - When true, use Defera Capital deposit bands; otherwise Slice Pay bands.
 * @param {boolean} [params.respectStoredDepositPercentage] - When true, accept any 5–90 percentage even if the *current* lead-time tier no longer offers it. Pass for SAVED bookings whose deposit was locked at booking time so the UI doesn't drift to a higher tier as the trip moves closer to departure (PY2RE8 canary: booked at 62 days with 30% deposit → at 56 days the snap-up rendered $535.25 vs the locked $401.44).
 * @param {number} [params.insurancePremium] - Faye travel insurance premium, dollars. It is added after the hold fee and joins the remaining balance only: no fee is charged on it and the deposit never changes (supabase/functions/_shared/trip-insurance.js).
 * @returns {Object} Payment schedule object
 */
export function calculatePaymentSchedule({
  bookingDate,
  departureDate,
  totalWithFee,
  depositPercentage,
  customDepositAmount,
  firstPaymentDays,
  daysBeforeDepartureForLastPayment = 14,
  holdFeeRate = SLICE_PAY_HOLD_FEE_RATE,
  deferaCapitalDepositTiers = false,
  respectStoredDepositPercentage = false,
  insurancePremium = 0,
}) {
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  // Convert dates to Date objects if strings
  const booking = typeof bookingDate === 'string' ? parseLocalDate(bookingDate) : new Date(bookingDate);
  const departure = typeof departureDate === 'string' ? parseLocalDate(departureDate) : new Date(departureDate);
  
  // Normalize dates to start of day
  booking.setHours(0, 0, 0, 0);
  departure.setHours(0, 0, 0, 0);
  
  // Calculate days between booking and departure
  const daysUntilDeparture = Math.floor((departure - booking) / (1000 * 60 * 60 * 24));
  
  // RULE 1: Minimum lead time - if less than 22 days, no payment plans allowed
  if (daysUntilDeparture < 22) {
    return {
      available: false,
      reason: 'insufficient_lead_time',
      daysUntilDeparture,
      depositOptions: [],
      weeklyPlan: { available: false },
      biweeklyPlan: { available: false }
    };
  }
  
  // RULE 2: Slice Pay vs Defera Capital deposit bands.
  // NOTE: 0% is allowed for refundable fares (Defera Zero / $0 down).
  let depositOptions = deferaCapitalDepositTiers
    ? getDeferaCapitalDepositOptionsForLeadDays(daysUntilDeparture)
    : getSlicePayDepositOptionsForLeadDays(daysUntilDeparture);
  
  // Validate deposit percentage
  // Explicitly allow 0 for refundable fares
  if (depositPercentage === 0) {
    depositOptions = [0];
  } else {
    if (depositPercentage != null && depositPercentage > 0) {
      // Accept custom percentages (admin-set), but cap at reasonable limits
      depositPercentage = Math.max(5, Math.min(90, depositPercentage));
    }
    // Snap to allowed tier when lowest tier is hidden for close-in trips, or value missing/invalid.
    // Skip the snap for saved bookings — the customer's deposit was locked at booking time and
    // shouldn't drift just because the trip moved into a tighter tier as departure approaches.
    const explicitlyValid = respectStoredDepositPercentage
      && Number.isFinite(depositPercentage)
      && depositPercentage >= 5
      && depositPercentage <= 90;
    if (!explicitlyValid && !depositOptions.includes(depositPercentage)) {
      depositPercentage = depositOptions[0];
    }
  }
  
  // totalWithFee = cash portion + service fee (miles_installments) or base fare + service fee (installments)
  const totalBeforeHoldFee = round2(totalWithFee);
  const holdFeeAmount = round2(totalBeforeHoldFee * holdFeeRate);
  const totalAfterHoldFee = round2(totalBeforeHoldFee + holdFeeAmount);
  
  // Deposit is % of pre-hold amount (cash portion + service fee), NOT total with hold
  const depositAmount = customDepositAmount && customDepositAmount > 0 
    ? round2(customDepositAmount) 
    : round2(totalBeforeHoldFee * (depositPercentage / 100));
  // Insurance rides on the installments after the deposit, outside every fee.
  const premium = Math.max(0, round2(insurancePremium));
  const totalWithInsurance = round2(totalAfterHoldFee + premium);
  const remainingBalance = round2(totalWithInsurance - depositAmount);
  
  // RULE 3: First payment dates
  // New Defera Zero (firstPaymentDays===7): first payment 7 days out for both weekly and bi-weekly
  // Legacy: Slice Pay weekly=day 8, bi-weekly=day 14
  const useSevenDayFirst = firstPaymentDays === 7;
  const weeklyFirstDays = useSevenDayFirst ? 7 : 8;
  const biweeklyFirstDays = useSevenDayFirst ? 7 : 14;
  const weeklyFirstPayment = new Date(booking);
  weeklyFirstPayment.setDate(weeklyFirstPayment.getDate() + weeklyFirstDays);
  
  const biweeklyFirstPayment = new Date(booking);
  biweeklyFirstPayment.setDate(biweeklyFirstPayment.getDate() + biweeklyFirstDays);
  
  // RULE 4: Last allowed payment date (see daysBeforeDepartureForLastPayment; Zero 21, Capital 5, Slice 14)
  const travelCutoff = new Date(departure);
  travelCutoff.setDate(travelCutoff.getDate() - daysBeforeDepartureForLastPayment);
  
  const maxWeeklyEnd = new Date(weeklyFirstPayment);
  maxWeeklyEnd.setDate(maxWeeklyEnd.getDate() + (25 * 7)); // 25 weeks = 26 payments total
  
  const maxBiweeklyEnd = new Date(biweeklyFirstPayment);
  maxBiweeklyEnd.setDate(maxBiweeklyEnd.getDate() + (24 * 14)); // 24 bi-weeks = 13 payments total
  
  const lastAllowedPaymentDate = new Date(Math.min(
    maxWeeklyEnd.getTime(),
    maxBiweeklyEnd.getTime(),
    travelCutoff.getTime()
  ));
  
  // RULE 5: Generate weekly payment schedule
  const weeklySchedule = [];
  let currentWeeklyDate = new Date(weeklyFirstPayment);
  while (currentWeeklyDate <= lastAllowedPaymentDate) {
    weeklySchedule.push(new Date(currentWeeklyDate));
    currentWeeklyDate.setDate(currentWeeklyDate.getDate() + 7);
  }
  
  const weeklyCount = weeklySchedule.length;
  const weeklyAmount = weeklyCount > 0 ? Math.round((remainingBalance / weeklyCount) * 100) / 100 : 0;
  
  const weeklyPlan = {
    available: weeklyCount >= 1 && weeklyCount <= 26,
    firstPaymentDate: weeklySchedule.length > 0 ? formatDate(weeklySchedule[0]) : null,
    finalPaymentDate: weeklySchedule.length > 0 ? formatDate(weeklySchedule[weeklySchedule.length - 1]) : null,
    paymentCount: weeklyCount,
    paymentAmount: weeklyAmount,
    schedule: weeklySchedule.map(formatDate)
  };
  
  // RULE 6: Generate bi-weekly payment schedule
  const biweeklySchedule = [];
  let currentBiweeklyDate = new Date(biweeklyFirstPayment);
  while (currentBiweeklyDate <= lastAllowedPaymentDate) {
    biweeklySchedule.push(new Date(currentBiweeklyDate));
    currentBiweeklyDate.setDate(currentBiweeklyDate.getDate() + 14);
  }
  
  const biweeklyCount = biweeklySchedule.length;
  const biweeklyAmount = biweeklyCount > 0 ? Math.round((remainingBalance / biweeklyCount) * 100) / 100 : 0;
  
  // Bi-weekly is only available if count >= 2
  const biweeklyPlan = {
    available: biweeklyCount >= 2 && biweeklyCount <= 13,
    firstPaymentDate: biweeklySchedule.length > 0 ? formatDate(biweeklySchedule[0]) : null,
    finalPaymentDate: biweeklySchedule.length > 0 ? formatDate(biweeklySchedule[biweeklySchedule.length - 1]) : null,
    paymentCount: biweeklyCount,
    paymentAmount: biweeklyAmount,
    schedule: biweeklySchedule.map(formatDate)
  };
  
  return {
    available: true,
    daysUntilDeparture,
    depositOptions,
    depositPercentage,
    depositAmount,
    remainingBalance,
    totalBeforeHoldFee,
    holdFeeRate,
    holdFeeAmount,
    totalAfterHoldFee,
    insurancePremium: premium,
    totalWithInsurance,
    weeklyPlan,
    biweeklyPlan
  };
}

/**
 * Default deposit % for search / list “Due today” estimates.
 * When `options.deferaCapital` is true, uses Capital bands; otherwise Slice Pay (20% at 60+ days, 30% at 30–59 days).
 * Capital defaults mirror the lowest available tier from
 * {@link getDeferaCapitalDepositOptionsForLeadDays} (effective 2026-06-16):
 *   90+ → 5; 60–89 → 20; 30–59 → 40; 22–29 → 50.
 */
export function getDefaultInstallmentDepositPercentForLeadDays(daysUntilDeparture, options = {}) {
  const deferaCapital = options.deferaCapital === true;
  if (!Number.isFinite(daysUntilDeparture)) return deferaCapital ? 5 : 20;
  if (daysUntilDeparture < 22) return 20;
  if (deferaCapital) {
    if (daysUntilDeparture < 30) return 60;
    if (daysUntilDeparture < 60) return 40;
    if (daysUntilDeparture < 90) return 20;
    return 5;
  }
  if (daysUntilDeparture < 60) return 30;
  return 20;
}

/**
 * Parse a date string treating date-only values (YYYY-MM-DD) as LOCAL midnight.
 *
 * `new Date('2026-10-30')` parses as UTC midnight, which in any negative-offset
 * timezone is the evening of Oct 29 local — so saved bookings (which store
 * `departure_date` as a date-only string) had their departure, and therefore the
 * last-payment cutoff, silently shifted one day earlier than what checkout
 * computed from the full `departing_at` datetime and what Stripe actually bills.
 * When the final installment landed exactly on departure − cutoff days, the
 * recomputed display dropped it entirely, showing the plan ending a full billing
 * interval early (e.g. final payment Oct 11 for an Oct 30 departure instead of
 * Oct 25). Mirrors `parseDepartureToLocalDate` in
 * `supabase/functions/_shared/defera-capital-deadline.ts` (3HV2NT-class fix).
 */
function parseLocalDate(value) {
  const ymd = String(value).trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  }
  return new Date(value);
}

/**
 * Format date to ISO string (YYYY-MM-DD)
 */
function formatDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

